import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';

interface RowDragAnDropProps<TSchema extends FieldValues> {
  data: TSchema[] | string[];
  setValue?: (field: string, value: TSchema[] | string[]) => void;
  dragIdentifierField?: keyof TSchema; // Field to use for drag identification (e.g., 'checkPointId')
  fieldName?: string; // Field name to use when calling setValue for object arrays
}

export const useRowDragAnDrop = <TSchema extends FieldValues>({
  data,
  setValue,
  dragIdentifierField,
  fieldName,
}: RowDragAnDropProps<TSchema>) => {
  const [rowData, setRowData] = useState(data);

  useEffect(() => {
    setRowData(data);
  }, [data]);

  const handleDragStart = (
    event: React.DragEvent<HTMLElement>,
    draggedItem: TSchema | string,
  ) => {
    if (typeof draggedItem === 'string') {
      event.dataTransfer.setData('text/plain', draggedItem);
    } else {
      event.dataTransfer.setData(
        'application/json',
        JSON.stringify(draggedItem),
      );
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.currentTarget.style.border = '2px solid #6101ee';
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.currentTarget.style.border = '';
  };

  const handleDrop = (
    event: React.DragEvent<HTMLElement>,
    targetItem: TSchema | string,
  ) => {
    event.preventDefault();
    event.currentTarget.style.border = '';

    let draggedItem: TSchema | string;

    try {
      draggedItem = JSON.parse(event.dataTransfer.getData('application/json'));
    } catch {
      draggedItem = event.dataTransfer.getData('text/plain');
    }

    if (!draggedItem || draggedItem === targetItem) return;

    if (Array.isArray(rowData) && typeof rowData[0] === 'string') {
      // Handle string[] case (reordering IDs)
      const updatedOrder = [...(rowData as string[])];
      const draggedIndex = updatedOrder.indexOf(draggedItem as string);
      const targetIndex = updatedOrder.indexOf(targetItem as string);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        updatedOrder.splice(draggedIndex, 1);
        updatedOrder.splice(targetIndex, 0, draggedItem as string);

        setRowData(updatedOrder);
        if (setValue) setValue('checkPointIds', updatedOrder);
      }
    } else {
      // Handle TSchema[] case (reordering objects)
      const updatedData = [...(rowData as TSchema[])];
      let draggedIndex = -1;
      let targetIndex = -1;

      if (
        dragIdentifierField &&
        typeof draggedItem === 'string' &&
        typeof targetItem === 'string'
      ) {
        // When dragging by a specific field (e.g., checkPointId)
        draggedIndex = updatedData.findIndex(
          (item) => item[dragIdentifierField] === draggedItem,
        );
        targetIndex = updatedData.findIndex(
          (item) => item[dragIdentifierField] === targetItem,
        );
      } else {
        // When dragging by object id
        draggedIndex = updatedData.findIndex(
          (item) => (item as TSchema).id === (draggedItem as TSchema).id,
        );
        targetIndex = updatedData.findIndex(
          (item) => (item as TSchema).id === (targetItem as TSchema).id,
        );
      }

      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Remove the dragged item from its current position
        const [movedItem] = updatedData.splice(draggedIndex, 1);
        // Insert it at the target position
        updatedData.splice(targetIndex, 0, movedItem);

        // Update index fields to reflect the new order
        const dataWithUpdatedIndexes = updatedData.map((item, index) => ({
          ...item,
          index,
        }));

        setRowData(dataWithUpdatedIndexes);
        if (setValue) setValue(fieldName || 'data', dataWithUpdatedIndexes);
      }
    }
  };

  return {
    rowData,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
